import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import Pagination from 'components/controls/Pagination';

import { useMediaQuery } from '@mui/system';
import ChevronDownIcon from 'mdi-react/ChevronDownIcon';
import ChevronUpIcon from 'mdi-react/ChevronUpIcon';
import moment from 'moment';
import React, { useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import {
  useGetAvailableJobsQuery,
  useGetFailuresQuery,
  useListJobsQuery,
  useRetryExecutionMutation,
} from 'store/app/entities/BatchSlice';
import alertActions from 'store/app/ui/Alert/action';
import { I18n } from 'utils/i18n';

// Helper to safely extract batchConfigId value (handles both string and object)
const getBatchConfigIdLabel = batchConfigId => {
  if (!batchConfigId) return null;
  if (typeof batchConfigId === 'string') return batchConfigId;
  return batchConfigId._id || batchConfigId.name || 'Batch Config';
};

const FailedJobRow = ({ execution, onRetry, onRetrySuccess }) => {
  const [expanded, setExpanded] = useState(false);

  const handleRetry = async () => {
    try {
      await onRetry(execution._id || execution.executionId).unwrap();
      onRetrySuccess();
    } catch (error) {
      console.error('Retry failed:', error);
    }
  };

  return (
    <Card sx={{ marginBottom: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, marginBottom: 1 }}>
              <Typography variant="h6">{execution.jobName}</Typography>
              <Chip label="Failed" color="error" size="small" variant="soft" />
              {execution.batchConfigId && (
                <Chip
                  label={getBatchConfigIdLabel(execution.batchConfigId)}
                  size="small"
                  variant="outlined"
                />
              )}
            </Box>
            <Typography variant="body2" color="textSecondary">
              {I18n.t('AdminBatchScene.FailedAt') || 'Failed at'}:{' '}
              {moment(execution.completedAt || execution.startedAt).format('YYYY-MM-DD HH:mm:ss')}
            </Typography>
            {execution.duration && (
              <Typography variant="body2" color="textSecondary">
                {I18n.t('AdminBatchScene.Duration') || 'Duration'}:{' '}
                {(execution.duration / 1000).toFixed(1)}s
              </Typography>
            )}
            {execution.error?.message && (
              <Typography variant="body2" color="error" sx={{ marginTop: 1 }}>
                {execution.error.message}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="outlined" color="primary" onClick={handleRetry}>
              {I18n.t('AdminBatchScene.Retry') || 'Retry'}
            </Button>
            <IconButton onClick={() => setExpanded(!expanded)} size="small">
              {expanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
            </IconButton>
          </Box>
        </Box>
        <Collapse in={expanded}>
          <Box
            sx={{
              marginTop: 2,
              padding: 2,
              backgroundColor: 'background.default',
              borderRadius: 1,
            }}
          >
            <Typography variant="subtitle2" sx={{ marginBottom: 1 }}>
              {I18n.t('AdminBatchScene.ErrorDetails') || 'Error Details'}
            </Typography>
            {execution.error && (
              <Box>
                {execution.error.name && (
                  <Typography variant="body2">
                    <strong>{I18n.t('AdminBatchScene.ErrorType') || 'Type'}:</strong>{' '}
                    {execution.error.name}
                  </Typography>
                )}
                {execution.error.message && (
                  <Typography variant="body2" sx={{ marginTop: 1 }}>
                    <strong>{I18n.t('AdminBatchScene.ErrorMessage') || 'Message'}:</strong>{' '}
                    {execution.error.message}
                  </Typography>
                )}
                {execution.error.stack && (
                  <Box sx={{ marginTop: 2 }}>
                    <Typography variant="body2">
                      <strong>{I18n.t('AdminBatchScene.StackTrace') || 'Stack Trace'}:</strong>
                    </Typography>
                    <pre
                      style={{
                        fontSize: '0.75rem',
                        overflow: 'auto',
                        backgroundColor: 'rgba(0,0,0,0.05)',
                        padding: '8px',
                        borderRadius: '4px',
                        marginTop: '8px',
                      }}
                    >
                      {execution.error.stack}
                    </pre>
                  </Box>
                )}
              </Box>
            )}
            {execution.workerId && (
              <Typography variant="body2" sx={{ marginTop: 1 }}>
                <strong>{I18n.t('AdminBatchScene.WorkerId') || 'Worker ID'}:</strong>{' '}
                <span style={{ fontFamily: 'monospace' }}>{execution.workerId}</span>
              </Typography>
            )}
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
};

const FailedJobsView = ({ actions }) => {
  const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');
  const [jobName, setJobName] = useState('');
  const [fromDate, setFromDate] = useState(() => {
    // Default to 7 days ago
    const date = new Date();
    date.setDate(date.getDate() - 7);
    return date.toISOString().split('T')[0];
  });
  const [page, setPage] = useState(0);
  const [perPage] = useState(25);
  const [retryExecution] = useRetryExecutionMutation();

  // Calculate days from selected date to today
  const days = fromDate ? Math.ceil((new Date() - new Date(fromDate)) / (1000 * 60 * 60 * 24)) : 7;
  const { data: availableJobs } = useGetAvailableJobsQuery();
  const { data: jobsData } = useListJobsQuery();

  // Handle different response structures and use listJobs as fallback
  let jobNames = [];
  if (availableJobs) {
    if (Array.isArray(availableJobs)) {
      jobNames = availableJobs;
    } else if (availableJobs.jobs && Array.isArray(availableJobs.jobs)) {
      jobNames = availableJobs.jobs;
    }
  }

  // Fallback: extract job names from listJobs query
  if (jobNames.length === 0 && jobsData?.jobs) {
    jobNames = jobsData.jobs.map(job => job.jobName || job.name).filter(Boolean);
  }

  const {
    data: failuresData,
    isLoading,
    refetch,
  } = useGetFailuresQuery({
    jobName: jobName || undefined,
    days,
    limit: perPage,
    offset: page * perPage,
  });

  const failures = failuresData?.failures || [];
  const totalCount = failuresData?.total || 0;

  // Note: WebSocket subscription is handled at parent level (AdminBatchScene)
  // RTK Query will automatically refetch failures when 'FailedJobs' tag is invalidated

  const handleRetrySuccess = () => {
    actions.alert.show({
      variant: 'success',
      label: I18n.t('AdminBatchScene.JobRetried') || 'Job Retried',
      message: I18n.t('AdminBatchScene.JobQueuedForRetry') || 'Job has been queued for retry',
    });
    refetch();
  };

  return (
    <Box>
      <Box
        sx={{ display: 'flex', gap: 2, marginBottom: 3, flexWrap: 'wrap', alignItems: 'center' }}
      >
        <FormControl sx={{ minWidth: 200 }} size="small">
          <InputLabel>{I18n.t('AdminBatchScene.FilterByJob') || 'Filter by Job'}</InputLabel>
          <Select value={jobName} onChange={e => setJobName(e.target.value)} label="Filter by Job">
            <MenuItem value="">{I18n.t('AdminBatchScene.AllJobs') || 'All Jobs'}</MenuItem>
            {jobNames.map(name => (
              <MenuItem key={name} value={name}>
                {name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          size="small"
          label={I18n.t('AdminBatchScene.FromDate') || 'From Date'}
          type="date"
          value={fromDate}
          onChange={e => setFromDate(e.target.value)}
          sx={{ width: 180, colorScheme: prefersDarkMode ? 'dark' : 'light' }}
          slotProps={{
            inputLabel: { shrink: true },
            input: { sx: { px: '14px', py: '8.5px' } },
            htmlInput: {
              min: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
              max: new Date().toISOString().split('T')[0],
            },
          }}
        />
        <Button variant="outlined" onClick={() => refetch()}>
          {I18n.t('AdminBatchScene.Refresh') || 'Refresh'}
        </Button>
      </Box>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', padding: 4 }}>
          <CircularProgress />
        </Box>
      ) : failures.length === 0 ? (
        <Box sx={{ textAlign: 'center', padding: 4 }}>
          <Typography variant="h6" color="textSecondary">
            {I18n.t('AdminBatchScene.NoFailures') || 'No failed jobs found'}
          </Typography>
        </Box>
      ) : (
        <Box>
          <Typography variant="h6" sx={{ marginBottom: 2 }}>
            {I18n.t('AdminBatchScene.FailedJobs') || 'Failed Jobs'} ({totalCount})
          </Typography>
          {failures.map(execution => (
            <FailedJobRow
              key={execution._id || execution.executionId}
              execution={execution}
              onRetry={retryExecution}
              onRetrySuccess={handleRetrySuccess}
            />
          ))}
          {totalCount > perPage && (
            <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: 3 }}>
              <Pagination
                total={totalCount}
                page={page}
                perPage={perPage}
                onChange={({ selected }) => setPage(selected)}
              />
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

const mapDispatchToProps = dispatch => ({
  actions: {
    alert: bindActionCreators(alertActions, dispatch),
  },
});

export default connect(null, mapDispatchToProps)(FailedJobsView);
